import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, Loader2 } from "lucide-react";
import { SonarGrid } from "@/components/ui/sonar-grid";

export default function Invitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { persistSession } = useAuth();
  const { theme } = useTheme();
  const brandLogo = theme === "dark" ? "/Logo_Full_White.webp" : "/Logo_Full_Black.webp";
  const [state, setState] = useState("loading"); // loading | ready | invalid
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/invite/${token}`)
      .then(({ data }) => { setInfo(data); setState("ready"); })
      .catch((err) => { setError(apiError(err.response?.data?.detail) || "Invitation invalide"); setState("invalid"); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Le mot de passe doit contenir au moins 8 caractères");
    if (password !== confirm) return toast.error("Les mots de passe ne correspondent pas");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/invite/${token}`, { password });
      persistSession(data);
      toast.success("Bienvenue dans l'équipe !");
      navigate("/app");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SonarGrid
      gradient={["#FF6A00", "#FFD500"]}
      className="flex min-h-screen items-center justify-center bg-background px-4"
    >
      <div className="relative z-10 grid w-full max-w-5xl overflow-hidden rounded-3xl border border-border bg-card shadow-2xl md:grid-cols-2">
        <div className="relative hidden flex-col justify-between bg-gradient-to-br from-[#FF6A00] to-[#FFD500] p-10 md:flex">
          <img src="/Logo_Mono_Black.webp" alt="HalgarOS" data-testid="invitation-brand-logo" className="h-10 w-auto self-start" />
          <div>
            <h1 className="font-display text-4xl font-bold leading-tight text-black">
              Vous rejoignez une belle équipe.
            </h1>
            <p className="mt-4 max-w-sm text-black/70">
              Créez votre mot de passe et accédez immédiatement à votre espace de travail HalgarOS.
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm font-medium text-black/70">
            <ShieldCheck className="h-4 w-4" /> Votre mot de passe reste confidentiel — même l'administrateur ne le voit pas.
          </div>
        </div>

        <div className="p-8 md:p-12">
          <img src={brandLogo} alt="HalgarOS" className="mb-6 h-9 w-auto md:hidden" />

          {state === "loading" && (
            <div data-testid="invite-loading" className="flex min-h-[16rem] items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Vérification de l'invitation…
            </div>
          )}

          {state === "invalid" && (
            <div data-testid="invite-invalid" className="min-h-[16rem]">
              <h2 className="font-display text-2xl font-bold text-foreground">Invitation indisponible</h2>
              <p className="mt-3 text-sm text-muted-foreground">{error}</p>
              <Button data-testid="invite-go-login" onClick={() => navigate("/login")} className="mt-8 rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">
                Aller à la connexion
              </Button>
            </div>
          )}

          {state === "ready" && info && (
            <>
              <h2 className="font-display text-2xl font-bold text-foreground">Bonjour {info.name}</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Vous êtes invité·e à rejoindre <strong className="text-foreground">{info.org_name}</strong> en tant que <strong className="text-foreground">{info.role}</strong>.
              </p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="invite-form">
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <Input data-testid="invite-email" value={info.email} disabled className="mt-1.5 border-border bg-muted opacity-70" />
                </div>
                <div>
                  <Label className="text-muted-foreground">Choisissez un mot de passe</Label>
                  <Input data-testid="invite-password" type="password" autoFocus className="mt-1.5 border-border bg-muted" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="8 caractères minimum" required />
                </div>
                <div>
                  <Label className="text-muted-foreground">Confirmez le mot de passe</Label>
                  <Input data-testid="invite-confirm" type="password" className="mt-1.5 border-border bg-muted" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
                </div>
                <Button data-testid="invite-submit" type="submit" disabled={submitting} className="w-full rounded-full bg-[#FF6A00] py-6 font-semibold text-white hover:bg-[#E65F00]">
                  {submitting ? "…" : "Activer mon compte"}
                </Button>
              </form>
              <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" /> Vous serez connecté·e automatiquement.
              </p>
            </>
          )}
        </div>
      </div>
    </SonarGrid>
  );
}
